import React, { useState } from 'react';
import { Auth } from 'aws-amplify';
import { PhoneValidation } from '../../utils/phoneValidation';

function validatePassword(password: string): string | null {
  if (password.length < 12) return 'Password must be at least 12 characters long';
  if (!/[a-z]/.test(password)) return 'Password must include a lowercase letter';
  if (!/[A-Z]/.test(password)) return 'Password must include an uppercase letter';
  if (!/[0-9]/.test(password)) return 'Password must include a number';
  if (!/[^A-Za-z0-9]/.test(password)) return 'Password must include a symbol';
  return null;
}

interface SignupFormProps {
  onSwitchToLogin: () => void;
  onSuccess: () => void;
  onVerificationNeeded: (email: string) => void;
}

export default function SignupForm({ onSwitchToLogin, onSuccess, onVerificationNeeded }: SignupFormProps) {
  const [formData, setFormData] = useState({
    email: '',
    organizationName: '',
    phoneNumber: '',
    password: '',
    confirmPassword: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setIsLoading(false);
      return;
    }

    // Validate password strength
    const passwordError = validatePassword(formData.password);
    if (passwordError) {
      setError(passwordError);
      setIsLoading(false);
      return;
    }

    // Validate phone number if provided
    if (formData.phoneNumber && formData.phoneNumber.trim()) {
      const phoneValidation = PhoneValidation.validateAndFormat(formData.phoneNumber);
      if (!phoneValidation.isValid) {
        setError(phoneValidation.errorMessage || 'Invalid phone number format');
        setIsLoading(false);
        return;
      }
    }

    try {
      // Format phone number to E.164 format if provided
      let formattedPhoneNumber = '';
      if (formData.phoneNumber && formData.phoneNumber.trim()) {
        formattedPhoneNumber = PhoneValidation.formatToE164(formData.phoneNumber);
      }

      const result = await Auth.signUp({
        username: formData.email,
        password: formData.password,
        attributes: {
          email: formData.email,
          phone_number: formattedPhoneNumber,
          'custom:tenantName': formData.organizationName,
        },
        autoSignIn: {
          enabled: true,
        },
      });

      // Check if user needs to verify their email
      if (result.user && !result.userConfirmed) {
        onVerificationNeeded(formData.email);
      } else {
        onSuccess();
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred during sign up');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-base-content mb-2">
          Create Account
        </h2>
        <p className="text-base-content/70">
          Get started with Pathbox today.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="alert alert-error">
            <svg 
              className="w-6 h-6" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
              />
            </svg>
            <span>{error}</span>
          </div>
        )}

        <div className="form-control">
          <label className="label">
            <span className="label-text font-medium">Email</span>
          </label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            className="input input-bordered w-full"
            placeholder="Enter your email"
            required
            disabled={isLoading}
          />
        </div>

        <div className="form-control">
          <label className="label">
            <span className="label-text font-medium">Organization Name</span>
          </label>
          <input
            type="text"
            name="organizationName"
            value={formData.organizationName}
            onChange={handleInputChange}
            className="input input-bordered w-full"
            placeholder="Enter your organization name"
            required
            disabled={isLoading}
          />
        </div>

        <div className="form-control">
          <label className="label">
            <span className="label-text font-medium">Phone Number (Optional)</span>
          </label>
          <input
            type="tel"
            name="phoneNumber"
            value={formData.phoneNumber}
            onChange={handleInputChange}
            className="input input-bordered w-full"
            placeholder="e.g., +1234567890, +44123456789, (555) 123-4567"
            disabled={isLoading}
          />
        </div>

        <div className="form-control">
          <label className="label">
            <span className="label-text font-medium">Password</span>
          </label>
          <input
            type="password"
            name="password"
            value={formData.password}
            onChange={handleInputChange}
            className="input input-bordered w-full"
            placeholder="Enter your password"
            required
            disabled={isLoading}
          />
          <label className="label">
            <span className="label-text-alt text-base-content/60">
              At least 12 characters, with uppercase, lowercase, a number, and a symbol
            </span>
          </label>
        </div>

        <div className="form-control">
          <label className="label">
            <span className="label-text font-medium">Confirm Password</span>
          </label>
          <input
            type="password"
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleInputChange}
            className="input input-bordered w-full"
            placeholder="Confirm your password"
            required
            disabled={isLoading}
          />
        </div>

        <div className="form-control">
          <button
            type="submit"
            className="btn btn-primary w-full"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span className="loading loading-spinner loading-sm"></span>
                Creating Account...
              </>
            ) : (
              'Create Account'
            )}
          </button>
        </div>
      </form>

      <div className="mt-8 text-center">
        <p className="text-base-content/70">
          Already have an account?{' '}
          <button
            onClick={onSwitchToLogin}
            className="link link-primary font-medium dark:text-primary-content dark:hover:text-primary-content/80"
            disabled={isLoading}
          >
            Sign in
          </button>
        </p>
      </div>
    </div>
  );
}
